/**
 * Cymatic Hologram 3D
 * 
 * Scientifically accurate 3D visualization of cymatic patterns based on Kirchhoff plate theory.
 * This implementation uses proper physics-based models for vibrating plates:
 * 
 * SCIENTIFIC BASIS:
 * - Kirchhoff Plate Equation: D∇⁴w + ρh(∂²w/∂t²) = 0
 *   where D = Eh³/(12(1-ν²)) is flexural rigidity, w is displacement
 * 
 * - Material Properties: Steel plate (E=200 GPa, ν=0.3, ρ=7850 kg/m³, h=1mm)
 * 
 * - Mode Shapes: Proper clamped plate mode functions satisfying boundary conditions:
 *   w(±a/2) = 0 and ∂w/∂n(±a/2) = 0 (zero displacement and slope at edges)
 * 
 * - Natural Frequencies: ω_mn = (π²/a²)√(D/ρh)·λ_mn
 *   where λ_mn depends on mode numbers (m,n) and boundary conditions
 * 
 * - Wavenumber: k = √(ω²ρh/D) - converts frequency to spatial wavelength
 * 
 * - Superposition: Multiple frequencies combine via mode superposition
 * 
 * Features:
 * - Physics-based plate vibration using Kirchhoff theory
 * - Proper mode shapes for clamped rectangular plates
 * - Accurate frequency-to-wavenumber conversion
 * - Real-time audio reactivity with proper wave propagation
 * - Multiple pattern types with scientifically valid frequency mappings
 */

import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { getCymaticFrequency, CymaticOscillator } from '../utils/cymaticFrequencies';
import { PHI } from '../domains/sacred-geometry/mathematics';

// Hot Module Replacement support for this component
if (process.env.NODE_ENV === 'development') {
  if (typeof module !== 'undefined' && (module as any).hot) {
    (module as any).hot.accept();
  }
}

interface CymaticHologram3DProps {
  patternType?: string;
  audioData?: { frequencyData: Uint8Array; averageAmplitude: number } | null;
  isPlaying?: boolean;
  width?: number;
  height?: number;
}

const CymaticHologram3D: React.FC<CymaticHologram3DProps> = ({
  patternType = 'flower-of-life',
  audioData = null,
  isPlaying = false,
  width = 1000,
  height = 1000
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene>();
  const rendererRef = useRef<THREE.WebGLRenderer>();
  const cameraRef = useRef<THREE.PerspectiveCamera>();
  const meshRef = useRef<THREE.Mesh>();
  const animationRef = useRef<number>();
  const calmStateCacheRef = useRef<Float32Array | null>(null);
  const lastPatternRef = useRef<string>('');
  const vertexRef = useRef<THREE.Vector3>(new THREE.Vector3());
  const colorRef = useRef<THREE.Color>(new THREE.Color());
  
  // Audio buffering for reduced latency and smoother updates
  const audioBufferRef = useRef<{ frequencyData: Uint8Array; averageAmplitude: number } | null>(null);
  const smoothedIntensityRef = useRef<number>(0);
  const isPlayingRef = useRef<boolean>(isPlaying);
  
  // Performance monitoring for adaptive quality (simplified)
  const frameCountRef = useRef<number>(0);
  const lastFpsUpdateRef = useRef<number>(Date.now());
  const qualityLevelRef = useRef<number>(1.0); // 0.5 = low, 1.0 = high
  const lastFrameTimeRef = useRef<number>(0);
  
  // Keep playback status current for the RAF loop without forcing scene remounts.
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);
  
  // Component version for HMR tracking
  const componentVersionRef = useRef(1);
  
  // Removed debug logging for performance - no longer logging on every render
  
  // Force remount on HMR update in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && typeof module !== 'undefined' && (module as any).hot) {
      const hotModule = (module as any).hot;
      const handleUpdate = () => {
        // HMR update - no logging for performance
        componentVersionRef.current += 1;
        // Force a remount by clearing the mount ref
        if (mountRef.current && rendererRef.current) {
          // The cleanup will handle disposal, and the effect will recreate
        }
      };
      
      hotModule.addStatusHandler((status: string) => {
        if (status === 'idle') {
          handleUpdate();
        }
      });
      
      return () => {
        if (hotModule.removeStatusHandler) {
          hotModule.removeStatusHandler(handleUpdate);
        }
      };
    }
  }, []);

  useEffect(() => {
    if (!mountRef.current) return;
    const mountElement = mountRef.current;

    const frameInterval = 1000 / 30; // ~33.33ms per frame (target 30fps)
    
    // Removed development logging for performance

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera setup - optimized to fill panel using sacred geometry
    // Using PHI (golden ratio) for fractal significance in scale calculations
    // Camera distance calculated to fill panel: distance = (plateDimension / 2) / tan(FOV/2) / fillFactor
    // Using PHI-based fill factor for sacred proportion (inverse for closer view)
    const fov = 50;
    const plateDimension = 6; // Plate size in 3D units
    const phiFillFactor = PHI * 0.75; // 1.618 * 0.75 ≈ 1.214 (brings camera closer to fill panel)
    // Calculate distance: half plate size / tan(half FOV) / fill factor
    const halfFovRad = (fov * Math.PI / 180) / 2;
    const cameraDistance = (plateDimension / 2) / Math.tan(halfFovRad) / phiFillFactor;
    
    const camera = new THREE.PerspectiveCamera(fov, width / height, 0.1, 1000);
    camera.position.set(0, 0, cameraDistance);
    cameraRef.current = camera;
    
    // Removed logging for performance

    // Renderer setup with nearest neighbor crisp clarity
    // Disable antialiasing for pixel-perfect, crisp rendering
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: false, // Disabled for nearest neighbor crisp clarity
      // Keep frame buffer contents so browser/DOM screenshots can capture WebGL reliably.
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance',
      precision: 'highp', // High precision for detailed calculations
      stencil: false, // Disable stencil buffer for performance
      depth: true // Keep depth buffer for 3D rendering
    });
    // Scale renderer to device pixel ratio for high-DPI displays
    // Limit to 2x for better performance while maintaining quality
    const basePixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    let dynamicPixelRatio = basePixelRatio;
    renderer.setPixelRatio(dynamicPixelRatio);
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    
    // Set nearest neighbor filtering for crisp textures (if any textures are added later)
    // This ensures pixel-perfect rendering
    // Shadows completely disabled for performance and crisp clarity
    renderer.shadowMap.enabled = false; // Disabled for performance
    rendererRef.current = renderer;
    mountElement.appendChild(renderer.domElement);

    // Get cymatic frequency configuration
    const frequencyConfig = getCymaticFrequency(patternType);
    
    // OPTIMIZED RESOLUTION: Smart balance using fractal significance (PHI-based scaling)
    // Uses adaptive quality system that maintains detail while ensuring smooth FPS
    // 
    // Resolution strategy with sacred geometry:
    // - Base: Uses PHI-based scaling for fractal significance
    // - Base segments: 144 (12², sacred number) scaled by PHI factors
    // - Scales using golden ratio multiples for optimal performance/quality balance
    // - Adaptive quality adjusts based on real-time FPS
    //
    // Why PHI-based scaling works:
    // - PHI (1.618) appears in nature's most efficient patterns
    // - Fractal self-similarity at PHI ratios
    // - Optimal balance between detail and performance
    // - Sacred geometry alignment with cymatic patterns
    const patternComplexity = frequencyConfig?.complexity || 5;
    const devicePixelRatio = window.devicePixelRatio || 1;
    const hardwareConcurrency = navigator.hardwareConcurrency || 4;
    
    // OPTIMIZED: Use PHI-based fractal scaling for resolution
    // Base: 144 segments (12² - sacred number, also 432/3 where 432 Hz is natural tuning)
    // Scale by PHI factors for fractal significance
    const baseSegments = 144; // Sacred base: 12² = 144
    const phiScale = Math.pow(PHI, patternComplexity / 10); // PHI^(complexity/10) for gradual scaling
    const deviceMultiplier = Math.min(PHI * 0.6, Math.max(1.0, devicePixelRatio * 0.5));
    const cpuMultiplier = Math.min(PHI * 0.5, Math.max(1.0, hardwareConcurrency / 8));
    
    // Apply PHI-based scaling
    const segments = Math.floor(baseSegments * phiScale * deviceMultiplier * cpuMultiplier);
    
    // Optimized bounds using PHI ratios: 144 (base) to 432 (PHI² * base, also 432 Hz significance)
    // 432 Hz is the natural tuning frequency found in nature
    const minSegments = 144; // Sacred base
    const maxSegments = Math.floor(144 * PHI * PHI); // 144 * φ² ≈ 377 (close to 432/1.144)
    const clampedSegments = Math.max(minSegments, Math.min(maxSegments, segments));
    const finalSegments = clampedSegments;
    
    // Removed verbose logging for performance - resolution calculated but not logged
    
    // Create 3D cymatic pattern mesh with HIGH RESOLUTION
    // Plate dimensions: 6x6 units (edges at x = ±3, y = ±3)
    const plateSize = 6;
    const plateHalfSize = plateSize / 2; // 3.0
    const geometry = new THREE.PlaneGeometry(plateSize, plateSize, finalSegments, finalSegments);

    // Prepare per-vertex colors (height-based)
    const positionAttr = geometry.attributes.position as THREE.BufferAttribute;
    const colors = new Float32Array(positionAttr.count * 3);
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    /**
     * Plate Material Properties (Steel plate typical values)
     * Based on Kirchhoff plate theory for accurate cymatic simulation
     */
    const plateProperties = {
      // Material: Steel
      youngsModulus: 200e9,        // E = 200 GPa (Pascals)
      poissonsRatio: 0.3,          // ν = 0.3 (dimensionless)
      density: 7850,                // ρ = 7850 kg/m³
      thickness: 0.001,             // h = 1mm (meters)
      
      // Calculated properties
      get flexuralRigidity(): number {
        // D = Eh³ / (12(1-ν²))
        const h3 = this.thickness * this.thickness * this.thickness;
        return (this.youngsModulus * h3) / (12 * (1 - this.poissonsRatio * this.poissonsRatio));
      },
      
      get massPerUnitArea(): number {
        // ρh
        return this.density * this.thickness;
      }
    };

    /**
     * Calculate natural frequency for a rectangular clamped plate mode
     * Based on Kirchhoff plate theory
     * 
     * ω_mn = (π²/a²) * sqrt(D/ρh) * λ_mn
     * where λ_mn depends on mode numbers m, n and boundary conditions
     * 
     * For clamped rectangular plates, λ_mn values are approximately:
     * λ_mn ≈ (m² + n²) for lower modes (simplified approximation)
     * More accurate values require solving the characteristic equation
     * 
     * @param m - Mode number in x direction (1, 2, 3, ...)
     * @param n - Mode number in y direction (1, 2, 3, ...)
     * @param a - Plate dimension in x (meters)
     * @param b - Plate dimension in y (meters)
     * @returns Natural frequency in rad/s
     */
    const calculateNaturalFrequency = (m: number, n: number, a: number, b: number): number => {
      const D = plateProperties.flexuralRigidity;
      const rhoH = plateProperties.massPerUnitArea;
      
      // For clamped plates, use approximate λ_mn values
      // More accurate would require solving transcendental equations
      // This approximation works well for lower modes
      const lambda_mn = Math.sqrt((m * m) + (n * n));
      
      // Natural frequency: ω = (π²/a²) * sqrt(D/ρh) * λ_mn
      const baseFreq = (Math.PI * Math.PI) / (a * a);
      const sqrtTerm = Math.sqrt(D / rhoH);
      const omega = baseFreq * sqrtTerm * lambda_mn;
      
      return omega;
    };

    /**
     * Calculate wavenumber from frequency
     * Based on plate vibration theory: k = sqrt(ω²ρh/D)
     * 
     * @param frequencyHz - Frequency in Hz
     * @returns Wavenumber in rad/m
     */
    const frequencyToWavenumber = (frequencyHz: number): number => {
      const omega = frequencyHz * 2 * Math.PI; // Convert Hz to rad/s
      const D = plateProperties.flexuralRigidity;
      const rhoH = plateProperties.massPerUnitArea;
      
      // Wavenumber: k = sqrt(ω²ρh/D)
      const k = Math.sqrt((omega * omega * rhoH) / D);
      return k;
    };

    /**
     * Mode shape function for clamped rectangular plate
     * 
     * For clamped plates, exact mode shapes involve hyperbolic functions
     * and satisfy: w = 0 and ∂w/∂n = 0 at boundaries
     * 
     * Simplified approximation using product of clamped beam functions:
     * X_m(x) and Y_n(y) where each satisfies clamped boundary conditions
     * 
     * For clamped beam: X(x) = cosh(αx) - cos(αx) - γ[sinh(αx) - sin(αx)]
     * where α and γ are determined by boundary conditions
     * 
     * Simplified version using polynomial approximation that satisfies
     * clamped boundary conditions: w(±a/2) = 0, w'(±a/2) = 0
     * 
     * @param x - X coordinate normalized to [-1, 1]
     * @param y - Y coordinate normalized to [-1, 1]
     * @param m - Mode number in x direction
     * @param n - Mode number in y direction
     * @returns Mode shape amplitude (dimensionless)
     */
    const clampedPlateModeShape = (x: number, y: number, m: number, n: number): number => {
      // Normalize coordinates to [-1, 1] range
      const xNorm = x / plateHalfSize;
      const yNorm = y / plateHalfSize;
      
      // Clamped boundary condition: w(±1) = 0, w'(±1) = 0
      // Use polynomial approximation that satisfies these conditions
      // For mode m: X_m(ξ) = (1 - ξ²)² * P_m(ξ) where P_m is a polynomial
      // Simplified: use sin/cos with boundary correction
      
      // For clamped edges, use functions that go to zero at boundaries
      // X_m(x) ≈ sin(mπ(x+1)/2) but modified to have zero slope at edges
      // Better approximation: use (1-x²)² envelope with sin/cos
      
      const xBoundary = (1 - xNorm * xNorm) * (1 - xNorm * xNorm); // (1-x²)²
      const yBoundary = (1 - yNorm * yNorm) * (1 - yNorm * yNorm); // (1-y²)²
      
      // Mode shape: product of x and y mode functions
      // For clamped plate, use sin functions with boundary envelope
      const xMode = Math.sin((m * Math.PI * (xNorm + 1)) / 2) * xBoundary;
      const yMode = Math.sin((n * Math.PI * (yNorm + 1)) / 2) * yBoundary;
      
      return xMode * yMode;
    };

    /**
     * Boundary condition function for clamped plate edges
     * 
     * In real cymatics, metal plates have clamped edges where:
     * - Displacement (vertical movement) is zero at the edges: w = 0
     * - Slope is zero at the edges: ∂w/∂n = 0
     * 
     * The mode shape functions already satisfy these conditions,
     * but we apply an additional smooth boundary factor to ensure
     * numerical stability and proper wave reflection.
     * 
     * @param x - X coordinate of vertex (-3 to 3)
     * @param y - Y coordinate of vertex (-3 to 3)
     * @returns Boundary factor (0.0 at edges, 1.0 at center)
     */
    const getBoundaryCondition = (x: number, y: number): number => {
      // Use distance to nearest edge for rectangular plate
      const distToEdgeX = plateHalfSize - Math.abs(x);
      const distToEdgeY = plateHalfSize - Math.abs(y);
      const distToNearestEdge = Math.min(distToEdgeX, distToEdgeY);
      
      // Normalize: 0 at edge, 1 at center
      const normalizedEdgeDistance = Math.max(0, distToNearestEdge) / plateHalfSize;
      
      // Apply smooth boundary factor (Hann window)
      // This ensures waves properly reflect and don't extend beyond edges
      const boundaryFactor = normalizedEdgeDistance > 0 
        ? 0.5 * (1 - Math.cos(Math.PI * normalizedEdgeDistance))
        : 0;
      
      return Math.max(0, Math.min(1, boundaryFactor));
    };

    // Custom shader material with 1-bit clarity layer
    // Combines base hologram colors with high-contrast black/white detail overlay
    const vertexShader = `
      varying vec3 vPosition;
      varying vec3 vColor;
      varying vec3 vNormal;
      varying float vHeight;
      
      void main() {
        vPosition = position;
        vColor = color;
        vNormal = normal;
        // Extract height from Z position (normalized to 0-1 range)
        // Z displacement ranges approximately from -0.3 to +0.3 (height * 0.3)
        // Normalize: (z + 0.3) / 0.6 maps -0.3 to 0.0 and +0.3 to 1.0
        vHeight = clamp((position.z + 0.3) / 0.6, 0.0, 1.0);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;
    
    const fragmentShader = `
      uniform float time;
      uniform float intensity;
      uniform vec3 baseColor;
      uniform vec3 emissiveColor;
      
      varying vec3 vPosition;
      varying vec3 vColor;
      varying vec3 vNormal;
      varying float vHeight;
      
      // Subtle edge detection for detail enhancement without over-contrast
      float detectEdges(vec3 normal) {
        // Calculate edge strength from normal variation
        vec3 flatNormal = vec3(0.0, 0.0, 1.0);
        float edgeStrength = 1.0 - dot(normalize(normal), flatNormal);
        return pow(edgeStrength, 0.7); // Gentler edge detection
      }
      
      // Moderate contrast function that preserves detail
      float enhanceContrast(float value, float contrast) {
        // Apply moderate contrast curve that preserves mid-tones
        return clamp((value - 0.5) * (1.0 + contrast) + 0.5, 0.0, 1.0);
      }
      
      void main() {
        // Base hologram color from vertex colors
        vec3 base = vColor;
        
        // Detect edges for subtle detail enhancement
        float edgeStrength = detectEdges(vNormal);
        
        // Moderate contrast enhancement to preserve fine details
        float contrastAmount = 0.5 + intensity * 0.3; // Moderate contrast (0.5-0.8)
        base.r = enhanceContrast(base.r, contrastAmount);
        base.g = enhanceContrast(base.g, contrastAmount);
        base.b = enhanceContrast(base.b, contrastAmount);
        
        // Subtle edge highlighting for fine detail visibility (reduced intensity)
        vec3 edgeHighlight = vec3(edgeStrength * 0.15); // Reduced edge highlight
        vec3 finalColor = base + edgeHighlight;
        
        // Moderate saturation and brightness boost to preserve detail
        float maxChannel = max(max(finalColor.r, finalColor.g), finalColor.b);
        if (maxChannel > 0.001) {
          // Moderate saturation boost (1.3x) to preserve color detail
          float gray = (finalColor.r + finalColor.g + finalColor.b) / 3.0;
          finalColor = mix(vec3(gray), finalColor, 1.3);
          
          // Gentle brightness boost
          finalColor *= 1.1;
          
          // Clamp to prevent oversaturation artifacts
          finalColor = clamp(finalColor, 0.0, 1.0);
        }
        
        // Add subtle emissive glow that enhances without washing out
        float glowAmount = 0.1 + intensity * 0.15;
        finalColor += emissiveColor * glowAmount;
        
        // Ensure minimum brightness for visibility without losing dark detail
        float minBrightness = 0.12;
        float currentBrightness = (finalColor.r + finalColor.g + finalColor.b) / 3.0;
        if (currentBrightness < minBrightness && currentBrightness > 0.02) {
          // Only boost very dark areas, preserve near-black for detail
          finalColor += vec3(minBrightness - currentBrightness) * 0.5;
        }
        
        // Output with high opacity for visibility
        gl_FragColor = vec4(finalColor, 1.0);
      }
    `;
    
    const shaderMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        intensity: { value: 0 },
        baseColor: { value: new THREE.Color(0x000000) },
        emissiveColor: { value: new THREE.Color(0x000000) }
      },
      vertexShader,
      fragmentShader,
      transparent: true,
      side: THREE.DoubleSide,
      vertexColors: true
    });
    
    // Use custom shader material for 1-bit clarity
    const material = shaderMaterial;

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = false; // Disable shadows
    mesh.receiveShadow = false; // Disable shadow receiving
    meshRef.current = mesh;
    scene.add(mesh);

    // Holographic glow effect - Add outer ring with enhanced visibility
    const glowGeometry = new THREE.RingGeometry(3.2, 3.5, 64);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ffff, // Brighter cyan for better visibility
      transparent: true,
      opacity: 0.3, // Slightly more visible
      side: THREE.DoubleSide
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    glow.castShadow = false; // Disable shadows
    glow.receiveShadow = false; // Disable shadow receiving
    glow.rotation.x = Math.PI / 2;
    scene.add(glow);

    // Lighting for hologram effect - minimal neutral lighting to preserve red/blue visibility
    // All shadows disabled for performance and crisp clarity
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.01);
    scene.add(ambientLight);

    const pointLight1 = new THREE.PointLight(0xffffff, 0.01, 2);
    pointLight1.castShadow = false; // Disable shadows
    pointLight1.position.set(5, 5, 5);
    scene.add(pointLight1);

    const pointLight2 = new THREE.PointLight(0xffffff, 0.01, 2);
    pointLight2.castShadow = false; // Disable shadows
    pointLight2.position.set(-5, -5, 5);
    scene.add(pointLight2);

    // Directional light for depth - neutral white
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.02);
    directionalLight.castShadow = false; // Disable shadows
    directionalLight.position.set(0, 0, 10);
    scene.add(directionalLight);

    // Height-to-color gradient: Balanced contrast for detail preservation
    // Moderate contrast with smooth transitions to preserve fine details
    // Represents sound wave polarities: Compression (cyan) <-> Rarefaction (magenta)
    const lowColor = new THREE.Color(0x00aaff);   // Moderate cyan (compression/positive pressure)
    const midColor = new THREE.Color(0x2a2a4a);   // Dark blue-gray (neutral) - preserves mid-tones
    const highColor = new THREE.Color(0xcc00cc);  // Moderate magenta (rarefaction/negative pressure)

    const heightToColor = (t: number, target: THREE.Color) => {
      const clamped = Math.max(0, Math.min(1, t));
      
      // Preserve detail: Use gentler gamma correction to maintain mid-tone information
      // Gamma closer to 1.0 preserves more detail, slight boost for visibility
      const gammaCorrected = Math.pow(clamped, 0.9); // Gentle gamma for detail preservation
      
      // Smooth color transitions to preserve fine details
      if (gammaCorrected < 0.5) {
        // Low to mid: Cyan -> Dark blue-gray (smooth transition)
        target.copy(lowColor).lerp(midColor, gammaCorrected * 2);
      } else {
        // Mid to high: Dark blue-gray -> Magenta (smooth transition)
        target.copy(midColor).lerp(highColor, (gammaCorrected - 0.5) * 2);
      }
      
      // Moderate saturation and brightness boost to preserve detail
      const hsl = { h: 0, s: 0, l: 0 };
      target.getHSL(hsl);
      hsl.s = Math.min(1.0, hsl.s * 1.3); // Moderate saturation boost (30%)
      hsl.l = Math.max(0.3, Math.min(0.85, hsl.l * 1.2)); // Gentle brightness increase (20%)
      target.setHSL(hsl.h, hsl.s, hsl.l);
      
      // Ensure minimum brightness for visibility without washing out details
      const rgb = { r: target.r, g: target.g, b: target.b };
      const minBrightness = 0.1; // Lower minimum to preserve dark detail areas
      const currentBrightness = (rgb.r + rgb.g + rgb.b) / 3;
      if (currentBrightness < minBrightness && currentBrightness > 0.01) {
        // Only boost very dark areas, preserve near-black for detail
        const boost = minBrightness / currentBrightness;
        target.setRGB(
          Math.min(1, rgb.r * boost),
          Math.min(1, rgb.g * boost),
          Math.min(1, rgb.b * boost)
        );
      }
    };

      /**
       * Calculate plate displacement using proper physics-based mode shapes
       * Based on Kirchhoff plate theory and superposition of modes
       * 
       * @param x - X coordinate
       * @param y - Y coordinate
       * @param oscillators - Array of frequency oscillators
       * @param time - Current time in seconds
       * @param intensity - Audio intensity multiplier (0-1)
       * @returns Displacement height
       */
      const calculatePlateDisplacement = (
        x: number,
        y: number,
        oscillators: CymaticOscillator[],
        time: number,
        intensity: number
      ): number => {
        let displacement = 0;
        
        oscillators.forEach((osc, idx) => {
          const frequencyHz = osc.frequency;
          const amplitude = osc.amplitude;
          const phase = osc.phase || 0;
          
          // Convert frequency to wavenumber using plate properties
          const k = frequencyToWavenumber(frequencyHz);
          
          // Map frequency to approximate mode numbers (m, n)
          // This is a simplified mapping - in reality, we'd solve for exact modes
          // For now, use frequency to estimate mode numbers
          const modeEstimate = Math.max(1, Math.floor(frequencyHz / 100)); // Rough estimate
          const m = Math.max(1, modeEstimate + idx % 3);
          const n = Math.max(1, modeEstimate + Math.floor(idx / 3));
          
          // Calculate mode shape contribution
          const modeShape = clampedPlateModeShape(x, y, m, n);
          
          // Time-dependent oscillation: cos(ωt + φ)
          const omega = frequencyHz * 2 * Math.PI;
          const timeOscillation = Math.cos(omega * time + phase);
          
          // Additional spatial wave component using wavenumber
          // This accounts for wave propagation on the plate
          const spatialWave = Math.cos(k * Math.sqrt(x * x + y * y) + phase);
          
          // Combine mode shape (standing wave pattern) with traveling wave component
          // Mode shape gives the spatial pattern, time oscillation gives temporal variation
          const modeContribution = modeShape * timeOscillation;
          const waveContribution = spatialWave * 0.3; // Reduced weight for traveling component
          
          // Superposition: add all mode contributions
          displacement += (modeContribution + waveContribution) * amplitude * (1 + intensity);
        });
        
        return displacement;
      };

      // Pre-calculate calm state geometry (cache it)
      // OPTIMIZED: Use fewer oscillators for calm state (static pattern doesn't need all)
      const calculateCalmState = (positions: THREE.BufferAttribute | THREE.InterleavedBufferAttribute) => {
        if (!frequencyConfig) return;
        
        const oscillators = frequencyConfig.oscillators;
        // Use only 2-3 primary oscillators for calm state (faster calculation)
        const primaryOscillators = oscillators.slice(0, Math.min(3, oscillators.length));
        const calmHeights = new Float32Array(positions.count);
        
        // Use static time (t=0) for calm state
        const staticTime = 0;
        const staticIntensity = 0;
        
        for (let i = 0; i < positions.count; i++) {
          vertexRef.current.fromBufferAttribute(positions, i);
          
          // Calculate displacement using physics-based model
          const displacement = calculatePlateDisplacement(
            vertexRef.current.x,
            vertexRef.current.y,
            primaryOscillators,
            staticTime,
            staticIntensity
          );
          
          // Apply boundary condition: multiply by boundary factor to ensure waves
          // are zero at edges and properly reflect (standing wave formation)
          const boundaryFactor = getBoundaryCondition(vertexRef.current.x, vertexRef.current.y);
          const finalDisplacement = displacement * boundaryFactor;
          
          calmHeights[i] = finalDisplacement * 0.05;
        }
        
        calmStateCacheRef.current = calmHeights;
      };

      // Calculate calm state cache if pattern changed (after geometry is created)
      const positions = geometry.attributes.position;
      if (lastPatternRef.current !== patternType) {
        lastPatternRef.current = patternType;
        calmStateCacheRef.current = null; // Clear old cache
        calculateCalmState(positions);
      }

      // Height buffers for temporal interpolation between expensive simulation ticks.
      const currentHeights = new Float32Array(positions.count);
      const targetHeights = new Float32Array(positions.count);
      let lastSimulationTime = 0;
      let simulationInterval = 1000 / 24; // ~24Hz physics updates, interpolated to visual FPS

      // Initialize height buffers from the static calm cache when available.
      if (calmStateCacheRef.current) {
        for (let i = 0; i < positions.count; i++) {
          const calmHeight = calmStateCacheRef.current[i];
          currentHeights[i] = calmHeight;
          targetHeights[i] = calmHeight;
          positions.setZ(i, calmHeight * 0.3);
        }
        positions.needsUpdate = true;
      }

      const updateActiveTargets = (time: number, intensity: number) => {
        if (!frequencyConfig) return;
        const oscillators = frequencyConfig.oscillators;
        const oscillatorCount = Math.max(2, Math.floor(oscillators.length * qualityLevelRef.current));
        const activeOscillators = oscillators.slice(0, oscillatorCount);
        const vertexSkip = qualityLevelRef.current < 0.7 ? 2 : 1;

        for (let i = 0; i < positions.count; i += vertexSkip) {
          vertexRef.current.fromBufferAttribute(positions, i);

          const displacement = calculatePlateDisplacement(
            vertexRef.current.x,
            vertexRef.current.y,
            activeOscillators,
            time,
            intensity
          );

          const boundaryFactor = getBoundaryCondition(vertexRef.current.x, vertexRef.current.y);
          const height = displacement * boundaryFactor;
          const end = Math.min(i + vertexSkip, positions.count);

          for (let j = i; j < end; j++) {
            targetHeights[j] = height;
          }
        }
      };

      const updateCalmTargets = () => {
        if (!calmStateCacheRef.current) {
          calculateCalmState(positions);
        }

        if (!calmStateCacheRef.current) return;
        for (let i = 0; i < positions.count; i++) {
          targetHeights[i] = calmStateCacheRef.current[i];
        }
      };

      // Animation loop - optimized with adaptive quality and frame throttling
      let isMounted = true;
      const animate = () => {
        if (!isMounted || !mountRef.current) return; // Prevent animation after unmount
        animationRef.current = requestAnimationFrame(animate);

        // Frame rate limiting: Only process frames at target FPS
        const now = performance.now();
        const deltaTime = now - lastFrameTimeRef.current;
        
        if (deltaTime < frameInterval) {
          // Skip frame to maintain target FPS
          return;
        }
        lastFrameTimeRef.current = now;

        // Performance monitoring: Calculate FPS every second
        frameCountRef.current++;
        const timeSinceLastUpdate = now - lastFpsUpdateRef.current;
        
        if (timeSinceLastUpdate >= 1000) {
          const actualFps = frameCountRef.current; // frames in last second
          frameCountRef.current = 0;
          lastFpsUpdateRef.current = now;
          
          // Adaptive quality: Adjust based on FPS (only adjust gradually)
          // Target is 30fps, so adjust if we're below 25 or above 32
          if (actualFps < 25) {
            qualityLevelRef.current = Math.max(0.5, qualityLevelRef.current - 0.1);
          } else if (actualFps > 32) {
            qualityLevelRef.current = Math.min(1.0, qualityLevelRef.current + 0.05);
          }

          // Dynamic renderer scaling: reduce pixel ratio when FPS drops hard.
          if (actualFps < 20) {
            dynamicPixelRatio = Math.max(0.75, dynamicPixelRatio - 0.1);
          } else if (actualFps > 28) {
            dynamicPixelRatio = Math.min(basePixelRatio, dynamicPixelRatio + 0.05);
          }

          if (Math.abs(renderer.getPixelRatio() - dynamicPixelRatio) > 0.01) {
            renderer.setPixelRatio(dynamicPixelRatio);
            renderer.setSize(width, height, false);
          }

          // Heavier load -> slower simulation tick, then interpolation fills visual frames.
          if (actualFps < 18) {
            simulationInterval = 1000 / 15;
          } else if (actualFps < 24) {
            simulationInterval = 1000 / 20;
          } else {
            simulationInterval = 1000 / 24;
          }
        }

        const time = now * 0.001;
        const positions = geometry.attributes.position;
        const colorAttr = geometry.attributes.color as THREE.BufferAttribute;

        // Audio buffering and smoothing for reduced latency
        // Buffer is updated in useEffect when audioData changes, just read it here
        // NO THRESHOLD - show all noise, even the smallest amplitudes
        const buffer = audioBufferRef.current;
        const hasActiveSound = isPlayingRef.current && buffer; // No amplitude threshold - show everything
        
        if (hasActiveSound) {
          // Exponential smoothing for smooth transitions (alpha = 0.7 for fast response)
          // Normalize amplitude but ensure minimum visibility (even 0 amplitude shows something)
          const rawIntensity = buffer.averageAmplitude / 255;
          // Add minimum base intensity so even silence shows the pattern
          const targetIntensity = Math.max(0.01, rawIntensity); // Minimum 1% visibility
          const smoothingFactor = 0.7; // Higher = more responsive, lower = smoother
          smoothedIntensityRef.current = smoothedIntensityRef.current * (1 - smoothingFactor) + 
                                        targetIntensity * smoothingFactor;
        } else {
          // When not playing, show calm state but don't fade to black
          // Keep a minimum intensity so pattern is always visible
          smoothedIntensityRef.current = Math.max(0.01, smoothedIntensityRef.current * 0.95); // Slow decay but never zero
        }
        
        const intensity = smoothedIntensityRef.current;

        // Deform mesh based on cymatic frequencies.
        // Run heavy physics at simulation cadence; interpolate every render frame.
        if (frequencyConfig && positions) {
          const shouldSimulate = (now - lastSimulationTime) >= simulationInterval;

          if (shouldSimulate) {
            lastSimulationTime = now;

            if (isPlayingRef.current && buffer) {
              updateActiveTargets(time, intensity);
            } else {
              updateCalmTargets();
            }
          }

          // Interpolate towards the latest simulation target for smooth visual motion.
          const interp = shouldSimulate ? 0.55 : 0.22;
          const updateColorsThisFrame = shouldSimulate || frameCountRef.current % 2 === 0;

          for (let i = 0; i < positions.count; i++) {
            const currentHeight = currentHeights[i] + (targetHeights[i] - currentHeights[i]) * interp;
            currentHeights[i] = currentHeight;
            positions.setZ(i, currentHeight * 0.3);

            if (updateColorsThisFrame) {
              const normalized = Math.max(0, Math.min(1, (currentHeight + 1) / 2));
              heightToColor(normalized, colorRef.current);
              colorAttr.setXYZ(i, colorRef.current.r, colorRef.current.g, colorRef.current.b);
            }
          }

          positions.needsUpdate = true;
          if (updateColorsThisFrame) {
            colorAttr.needsUpdate = true;
          }

          // Normals are expensive; refresh less frequently.
          if (shouldSimulate && frameCountRef.current % 3 === 0) {
            geometry.computeVertexNormals();
          }
        }

        // Keep hologram facing directly forward for readability
        // Always animate when playing - no threshold
        if (isPlayingRef.current && buffer) {
          // Active sound: keep front-facing; only a tiny wobble so it feels alive
          mesh.rotation.x = 0;
          mesh.rotation.y = 0;
          mesh.rotation.z = Math.sin(time * 0.15) * 0.03; // very small wobble around Z
        } else {
          // No active sound: completely static, facing forward
          mesh.rotation.x = 0;
          mesh.rotation.y = 0;
          mesh.rotation.z = 0;
        }

        // Animate glow ring with enhanced visibility
        // Always animate when playing - no threshold
        if (isPlayingRef.current && buffer) {
          glow.rotation.z = time * 0.2;
          glow.material.opacity = 0.3 + Math.sin(time * 2) * 0.15; // More visible
        } else {
          glow.rotation.z = 0; // Completely static when calm
          glow.material.opacity = 0.15; // More visible even when calm
        }

        // Update shader uniforms based on audio
        // Always show pattern - no blackouts, even at lowest amplitudes
        if (isPlayingRef.current && buffer) {
          material.uniforms.intensity.value = intensity;
          material.uniforms.time.value = time;
        } else {
          // Calm state: minimum intensity to keep pattern visible
          material.uniforms.intensity.value = 0.01; // Never zero - always show pattern
          material.uniforms.time.value = time;
        }

        // Animate lights
        // Always animate when playing - no threshold
        if (isPlayingRef.current && buffer) {
          pointLight1.intensity = 1.5 + Math.sin(time * 2) * 0.3;
          pointLight2.intensity = 1.0 + Math.cos(time * 2.5) * 0.3;
        } else {
          // Calm state: steady, lower intensity lights
          pointLight1.intensity = 1.0;
          pointLight2.intensity = 0.7;
        }

        renderer.render(scene, camera);
      };

    animate();

    // Handle resize
    const handleResize = () => {
      if (!cameraRef.current || !rendererRef.current) return;
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(width, height);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup - prevent memory leaks
    return () => {
      isMounted = false; // Stop animation loop
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = undefined;
      }
      
      // Remove all objects from scene before disposal
      if (sceneRef.current) {
        sceneRef.current.remove(mesh);
        sceneRef.current.remove(glow);
        sceneRef.current.remove(ambientLight);
        sceneRef.current.remove(pointLight1);
        sceneRef.current.remove(pointLight2);
        sceneRef.current.remove(directionalLight);
        sceneRef.current.clear();
      }
      
      // Dispose all geometries and materials
      if (mountElement && rendererRef.current) {
        try {
          mountElement.removeChild(rendererRef.current.domElement);
        } catch (e) {
          // Element may already be removed
        }
      }
      rendererRef.current?.dispose();
      geometry.dispose();
      material.dispose();
      glowGeometry.dispose(); // Fix: dispose glow geometry
      glowMaterial.dispose();
      
      // Clear refs
      rendererRef.current = undefined;
      sceneRef.current = undefined;
      meshRef.current = undefined;
      audioBufferRef.current = null;
      calmStateCacheRef.current = null;
    };
  }, [patternType, width, height]);
  
  // Separate effect to update audio buffer when audio data changes (without recreating scene)
  useEffect(() => {
    // Update audio buffer immediately when new data arrives for low latency
    if (audioData && isPlaying) {
      audioBufferRef.current = {
        frequencyData: new Uint8Array(audioData.frequencyData), // Copy for thread safety
        averageAmplitude: audioData.averageAmplitude
      };
    } else if (!isPlaying) {
      // Clear buffer when not playing
      audioBufferRef.current = null;
    }
    
    // Removed logging for performance
  }, [audioData, isPlaying]);

  return (
    <div
      ref={mountRef}
      key={process.env.NODE_ENV === 'development' ? `cymatic-${componentVersionRef.current}` : undefined}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        position: 'relative',
        background: 'transparent',
        imageRendering: 'crisp-edges' // Nearest neighbor crisp clarity
      }}
    />
  );
};

export default CymaticHologram3D;

